import urlJoin from 'url-join';
import user from 'yola-editor/src/js/modules/user';
import dialogs from 'yola-editor/src/js/modules/dialogs';
import auth from 'yola-editor/src/js/modules/auth';
import config from 'src/js/modules/config';
import segment from 'src/js/modules/analytics/segment';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import beforeUnloadListener from 'yola-editor/src/js/utils/before-unload-listener';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import getTemplateBuildSlugTrait from 'src/js/modules/analytics/segment/helpers/get-template-build-slug-trait';
import actionTypes from '../constants/action-types';
import productTypes from '../constants/product-types';
import upgradeTypes from '../constants/upgrade-types';
import { ONLINE_STORE } from '../constants/upsell-types';
import trackUpgradeRedirect from '../helpers/track-upgrade-redirect';
import trackUpgradeInApp from '../helpers/track-upgrade-in-app';
import upgradeDataStorage from '../helpers/upgrade-data-storage';
import actions from '../actions';
import getUpgradeData from '../selectors/upgrade-data';
import getPurchaseFlowType from '../helpers/get-purchase-flow-type';

const { constants, track, trackAsync } = segment;

const PREMIUM_FEATURE_ACTION_ID = 'premium-feature';

const upgrade = (store) => (next) => async (action) => {
  switch (action.type) {
    case actionTypes.TRIGGER_UPGRADE: {
      const state = store.getState();
      const { upsellType, editorUpgradeAction, details = {} } = action.payload;
      const siteId = auth.selectors.getSiteId(state);
      const availablePlatformComponents = user.selectors.getAvailablePlatformComponents(state);
      const productType =
        upsellType === ONLINE_STORE ? productTypes.ONLINE_STORE : productTypes.LATITUDE;
      const actionId = details.actionId || PREMIUM_FEATURE_ACTION_ID;
      const trackingData = {
        siteId,
        templateBuildSlug: getTemplateBuildSlugTrait(),
        premiumFeature: upsellType,
        purchaseFlowType: getPurchaseFlowType(state),
      };
      const upgradeDetails = { ...details, upsellType };

      upgradeDataStorage.setEditorUpgradeAction(editorUpgradeAction);
      upgradeDataStorage.setUpgradeDetails(upgradeDetails);

      const onCloseDialog = () => {
        editorUpgradeAction.reject();
        store.dispatch(dialogs.actions.hide());
        track(constants.events.PLAN_SELECTOR_BACK_TO_EDITOR_BUTTON_CLICKED, {
          ...trackingData,
          productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
        });
      };

      // b2c & reseller platform upgrade
      if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
        if (upsellType === ONLINE_STORE) {
          trackAsync(constants.events.UPGRADE_INITIATED, {
            ...trackingData,
            productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
            trial: false,
          }).then(() => {
            const checkoutserviceUrl = config.selectors.getCheckoutserviceUrl(state);
            beforeUnloadListener.remove();

            saveSiteWithEffect(() => {
              redirectToUrl(urlJoin(checkoutserviceUrl, '/initiate-payment/?products=ecommerce'));
            });
          });
          return;
        }

        const dialogProps = {
          upsellType,
          actionId,
          ...details,
          onBackButtonClick: () => onCloseDialog(),
          onClose: () => onCloseDialog(),
        };

        store.dispatch(dialogs.actions.show(dialogs.dialogTypes.PAYWALL, dialogProps));

        next(action);
        return;
      }

      // WL upgrade
      const onConfirmPurchase = (confirmedProduct) => {
        const { sku } = confirmedProduct;

        trackUpgradeInApp(trackingData);

        store.dispatch(
          actions.orderSubscriptionPlan({
            productType,
            sku,
          })
        );
      };

      if (user.selectors.getPartnerSubmitUserActionUrl(state)) {
        const upgradeData = getUpgradeData(state, productType);

        if (upgradeData.type === upgradeTypes.REDIRECT) {
          trackUpgradeRedirect(trackingData).then(() => {
            saveSiteWithEffect(() => {
              redirectToUrl(upgradeData.destination);

              // if user cancel redirect
              store.dispatch(dialogs.actions.hide());
              editorUpgradeAction.reject();
              next(action);
            });
          });
        } else if (upgradeData.type === upgradeTypes.IN_APP) {
          // start in-app purchase flow
          const { description, terms, options: products } = upgradeData;

          store.dispatch(
            dialogs.actions.show(dialogs.dialogTypes.IN_APP_PURCHASE, {
              description,
              terms,
              products,
              onCloseDialog,
              onConfirmPurchase,
            })
          );

          track(constants.events.PLAN_SELECTOR_DISPLAYED, {
            ...trackingData,
            actionId,
            productCategory: constants.common.EDITOR_PRODUCT_CATEGORY,
            activePromotions: [],
            trial: false,
          });

          next(action);
        } else {
          editorUpgradeAction.reject();
          next(action);
        }
      } else {
        const path = user.selectors.getPartnerUpgradeUrl(state);

        if (path) {
          // redirect user to the `partner.properties.upgrade_url`
          const { id: userId } = user.selectors.getUserData(state);
          const { id: partnerId } = user.selectors.getPartnerData(state);

          let queryString = `?u=${userId}&p=${partnerId}`;

          if (upsellType === ONLINE_STORE) {
            queryString = `${queryString}&action=upgrade&product=ecwid`;
          }

          trackUpgradeRedirect(trackingData).then(() => {
            saveSiteWithEffect(() => {
              redirectToUrl(`${path}${queryString}`, { topWindow: true });

              // if user cancel redirect
              store.dispatch(dialogs.actions.hide());
              editorUpgradeAction.reject();
              next(action);
            });
          });
        } else {
          // for local development
          editorUpgradeAction.resolve();
          next(action);
        }
      }

      break;
    }
    default:
      next(action);
  }
};

export default upgrade;
